import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import PropTypes from "prop-types";

// Material UI components
import {
  Grid,
  Card,
  CardMedia,
  Chip,
  Divider,
  Avatar,
  Skeleton,
  Stack,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  Fade,
  Grow,
  Zoom,
  Slide,
  Paper,
  Container,
} from "@mui/material";
import { keyframes } from "@mui/system";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import LinkIcon from "@mui/icons-material/Link";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import UpdateIcon from "@mui/icons-material/Update";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import CommentIcon from "@mui/icons-material/Comment";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import DescriptionIcon from "@mui/icons-material/Description";
import TitleIcon from "@mui/icons-material/Title";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import BASE_URL from "BASE_URL";

// Premium Animations
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeInLeft = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeInRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const rotateIn = keyframes`
  from { transform: rotate(-180deg) scale(0); opacity: 0; }
  to { transform: rotate(0deg) scale(1); opacity: 1; }
`;

function NewsDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const metaBlue = "#1565C0";
  const metaOrange = "#ED6C02";

  useEffect(() => {
    fetchNewsDetails();
  }, [id]);

  const fetchNewsDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/news/get-news-by-id?id=${id}`);
      if (response.data.success) {
        setNews(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching news details:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: news?.title,
        text: news?.sort_description,
        url: window.location.href,
      });
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mt={3}>
          <Container maxWidth="lg">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: "20px" }} />
              </Grid>
              <Grid item xs={12}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: "20px" }} />
              </Grid>
            </Grid>
          </Container>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!news) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Fade in={true}>
            <Card sx={{ p: 5, textAlign: "center", borderRadius: "28px", maxWidth: 400 }}>
              <NewspaperIcon sx={{ fontSize: 80, color: "#9e9e9e", mb: 2 }} />
              <MDTypography variant="h4" color="error" fontWeight="bold" gutterBottom>
                News Not Found
              </MDTypography>
              <MDTypography variant="body1" color="text" mb={3}>
                The news article you`&apos;re looking for doesn`&apos;t exist or has been removed.
              </MDTypography>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => navigate("/news")}
                sx={{ borderRadius: "30px", px: 4 }}
              >
                Back to News
              </MDButton>
            </Card>
          </Fade>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={2} mb={3}>
        {/* Header with Back Button */}
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <Zoom in={true} timeout={500}>
            <MDButton
              variant="gradient"
              color="dark"
              size="medium"
              onClick={() => navigate("/news")}
              startIcon={<ArrowBackIcon />}
              sx={{
                borderRadius: "30px",
                textTransform: "none",
                fontWeight: "bold",
                px: 3,
                transition: "all 0.3s",
                "&:hover": { transform: "translateX(-5px)" },
              }}
            >
              Back to News
            </MDButton>
          </Zoom>

          {/* <Zoom in={true} timeout={500} style={{ transitionDelay: "100ms" }}>
            <MDBox display="flex" gap={1}>
              <Tooltip title="Share">
                <IconButton
                  onClick={handleShare}
                  sx={{
                    bgcolor: "rgba(0,0,0,0.05)",
                    transition: "all 0.2s",
                    "&:hover": { transform: "scale(1.1)" },
                  }}
                >
                  <ShareIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={copied ? "Copied!" : "Copy Link"}>
                <IconButton
                  onClick={handleCopyLink}
                  sx={{
                    bgcolor: "rgba(0,0,0,0.05)",
                    transition: "all 0.2s",
                    "&:hover": { transform: "scale(1.1)" },
                  }}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print">
                <IconButton
                  onClick={handlePrint}
                  sx={{
                    bgcolor: "rgba(0,0,0,0.05)",
                    transition: "all 0.2s",
                    "&:hover": { transform: "scale(1.1)" },
                  }}
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </MDBox>
          </Zoom> */}
        </MDBox>

        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {/* Main Content */}
            <Grid item xs={12} lg={12}>
              <Fade in={true} timeout={800}>
                <Card
                  sx={{
                    borderRadius: "28px",
                    overflow: "hidden",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                    transition: "all 0.3s",
                    "&:hover": { boxShadow: "0 25px 50px rgba(0,0,0,0.15)" },
                  }}
                >
                  {/* Featured Image */}
                  {news.image && (
                    <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
                      <CardMedia
                        component="img"
                        image={news.image}
                        alt={news.title}
                        sx={{
                          width: "100%",
                          height: "auto",
                          maxHeight: "70vh",
                          objectFit: "cover",
                          objectPosition: "center",
                          transition: "transform 0.5s ease",
                          "&:hover": { transform: "scale(1.02)" },
                        }}
                      />
                      {/* Status Badge */}
                      <Zoom in={true} timeout={600}>
                        <Chip
                          label={news.status?.toUpperCase()}
                          size="medium"
                          sx={{
                            position: "absolute",
                            top: 20,
                            right: 20,
                            bgcolor: news.status === "active" ? "#2e7d32" : "#d32f2f",
                            color: "#fff",
                            fontWeight: "bold",
                            px: 2,
                            animation: `${pulse} 2s infinite`,
                          }}
                        />
                      </Zoom>
                    </Box>
                  )}

                  {/* Content */}
                  <Box sx={{ p: { xs: 3, md: 4 } }}>
                    {/* Title */}
                    <Zoom in={true} timeout={600}>
                      <MDTypography
                        variant="h3"
                        fontWeight="bold"
                        sx={{
                          mb: 2,
                          lineHeight: 1.3,
                          letterSpacing: "-0.5px",
                          animation: `${fadeInUp} 0.6s ease-out`,
                        }}
                      >
                        {news.title}
                      </MDTypography>
                    </Zoom>

                    {/* Meta Info */}
                    <Fade in={true} timeout={800}>
                      <Stack
                        direction="row"
                        spacing={2}
                        flexWrap="wrap"
                        sx={{ mb: 3, pb: 2, borderBottom: "1px solid #e0e0e0" }}
                      >
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: metaBlue }}>
                            <PersonIcon sx={{ fontSize: 18 }} />
                          </Avatar>
                          <MDTypography variant="body2" fontWeight="medium">
                            {news.author || "Admin"}
                          </MDTypography>
                        </MDBox>
                        {news.reference_by && (
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <LinkIcon sx={{ fontSize: 18, color: metaOrange }} />
                            <MDTypography variant="body2" fontWeight="medium">
                              Reference: {news.reference_by}
                            </MDTypography>
                          </MDBox>
                        )}
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <CalendarTodayIcon sx={{ fontSize: 18, color: metaBlue }} />
                          <MDTypography variant="body2" fontWeight="medium">
                            {formatDate(news.createdAt)}
                          </MDTypography>
                        </MDBox>
                      </Stack>
                    </Fade>

                    {/* Short Description */}
                    <Fade in={true} timeout={900}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          mb: 3,
                          bgcolor: "#f5f5f5",
                          borderRadius: "20px",
                          borderLeft: `4px solid ${metaBlue}`,
                        }}
                      >
                        <MDTypography
                          variant="body1"
                          fontWeight="bold"
                          color="primary"
                          gutterBottom
                        >
                          📌 Summary
                        </MDTypography>
                        <MDTypography variant="body1" lineHeight={1.7}>
                          {news.sort_description}
                        </MDTypography>
                      </Paper>
                    </Fade>

                    {/* Full Description */}
                    <Fade in={true} timeout={1000}>
                      <MDBox>
                        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                          <DescriptionIcon
                            sx={{ color: metaBlue, animation: `${rotateIn} 0.5s ease-out` }}
                          />
                          <MDTypography variant="h5" fontWeight="bold">
                            Full Article
                          </MDTypography>
                        </MDBox>
                        <Divider sx={{ mb: 3 }} />
                        <MDTypography
                          variant="button"
                          color="dark"
                          fontWeight="regular"
                          lineHeight={1.9}
                          sx={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontSize: "1.05rem",
                          }}
                        >
                          {news.description}
                        </MDTypography>
                      </MDBox>
                    </Fade>
                  </Box>
                </Card>
              </Fade>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} lg={4}>
              <Stack spacing={3}>
                {/* Author Card */}
                {/* <Slide direction="left" in={true} timeout={600}>
                  <Card
                    sx={{
                      borderRadius: "24px",
                      p: 3,
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      textAlign: "center",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        margin: "0 auto",
                        mb: 2,
                        bgcolor: "rgba(255,255,255,0.2)",
                        animation: `${float} 2s infinite`,
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 45 }} />
                    </Avatar>
                    <MDTypography variant="h6" fontWeight="bold">
                      {news.author || "Admin"}
                    </MDTypography>
                    <MDTypography variant="caption" opacity={0.9}>
                      Author
                    </MDTypography>
                    <Divider sx={{ my: 2, bgcolor: "rgba(255,255,255,0.2)" }} />
                    <MDTypography variant="body2" opacity={0.9}>
                      Published on {formatDate(news.createdAt).split(",")[0]}
                    </MDTypography>
                  </Card>
                </Slide> */}

                {/* Information Card */}
                {/* <Grow in={true} timeout={700}>
                  <Card sx={{ borderRadius: "24px", p: 3 }}>
                    <MDTypography variant="h6" fontWeight="bold" mb={2}>
                      <NewspaperIcon sx={{ mr: 1, verticalAlign: "middle", color: metaBlue }} />
                      Information
                    </MDTypography>
                    <Stack spacing={2}>
                      <MDBox display="flex" alignItems="center" gap={2}>
                        <VisibilityIcon sx={{ color: metaBlue }} />
                        <MDTypography variant="body2" fontWeight="medium">
                          Status: {news.status?.toUpperCase()}
                        </MDTypography>
                      </MDBox>
                      {news.reference_by && (
                        <MDBox display="flex" alignItems="center" gap={2}>
                          <LinkIcon sx={{ color: metaOrange }} />
                          <MDTypography variant="body2" fontWeight="medium">
                            Reference: {news.reference_by}
                          </MDTypography>
                        </MDBox>
                      )}
                      <MDBox display="flex" alignItems="center" gap={2}>
                        <CalendarTodayIcon sx={{ color: metaBlue }} />
                        <MDTypography variant="body2" fontWeight="medium">
                          Created: {formatDate(news.createdAt)}
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center" gap={2}>
                        <UpdateIcon sx={{ color: metaBlue }} />
                        <MDTypography variant="body2" fontWeight="medium">
                          Updated: {formatDate(news.updatedAt)}
                        </MDTypography>
                      </MDBox>
                    </Stack>
                  </Card>
                </Grow> */}

                {/* Share Card */}
                {/* <Fade in={true} timeout={800}>
                  <Card sx={{ borderRadius: "24px", p: 3, textAlign: "center" }}>
                    <MDTypography variant="h6" fontWeight="bold" mb={2}>
                      Share this Article
                    </MDTypography>
                    <MDBox display="flex" justifyContent="center" gap={2}>
                      <IconButton
                        onClick={handleShare}
                        sx={{
                          bgcolor: "#1877f2",
                          color: "white",
                          "&:hover": { transform: "scale(1.1)", bgcolor: "#166fe5" },
                        }}
                      >
                        <ShareIcon />
                      </IconButton>
                      <IconButton
                        onClick={handleCopyLink}
                        sx={{
                          bgcolor: "#6c757d",
                          color: "white",
                          "&:hover": { transform: "scale(1.1)", bgcolor: "#5a6268" },
                        }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                      <IconButton
                        onClick={handlePrint}
                        sx={{
                          bgcolor: "#17a2b8",
                          color: "white",
                          "&:hover": { transform: "scale(1.1)", bgcolor: "#138496" },
                        }}
                      >
                        <PrintIcon />
                      </IconButton>
                    </MDBox>
                    {copied && (
                      <MDTypography variant="caption" color="success" mt={1} display="block">
                        Link copied to clipboard!
                      </MDTypography>
                    )}
                  </Card>
                </Fade> */}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </MDBox>
    </DashboardLayout>
  );
}

export default NewsDetails;
